import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Plus, Search, Filter, Activity, Shield, Bot,
  AlertCircle, CheckCircle, Pause, Play, Settings,
  ChevronRight, Cpu, FileText, Eye, X, Lock, Sparkles
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';

const STATUS_CONFIG = {
  DRAFT: { color: 'gray', icon: FileText, label: 'Draft' },
  PROVISIONING: { color: 'blue', icon: Cpu, label: 'Provisioning' },
  CONFIGURING: { color: 'blue', icon: Settings, label: 'Configuring' },
  GOVERNANCE_SETUP: { color: 'purple', icon: Shield, label: 'Governance Setup' },
  COMPLIANCE_CHECK: { color: 'yellow', icon: AlertCircle, label: 'Compliance Check' },
  READY: { color: 'green', icon: CheckCircle, label: 'Ready' },
  ACTIVE: { color: 'green', icon: Activity, label: 'Active' },
  PAUSED: { color: 'yellow', icon: Pause, label: 'Paused' },
  SUSPENDED: { color: 'red', icon: AlertCircle, label: 'Suspended' },
  DECOMMISSIONED: { color: 'gray', icon: X, label: 'Decommissioned' }
};

const HEALTH_CONFIG = {
  HEALTHY: { color: 'green', label: 'Healthy' },
  DEGRADED: { color: 'yellow', label: 'Degraded' },
  UNHEALTHY: { color: 'red', label: 'Unhealthy' },
  UNKNOWN: { color: 'gray', label: 'Unknown' }
};

// Demo data for unauthorized users
const DEMO_EMPLOYEES = [
  {
    id: 'demo-1',
    name: 'Nellie',
    title: 'Chief Autonomy Officer (CAO)',
    employeeId: 'CAO-001',
    status: 'ACTIVE',
    autonomyTier: 'FULL_AUTONOMY',
    riskLevel: 'LOW',
    role: { name: 'Executive Leadership' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-1', status: 'RUNNING', healthStatus: 'HEALTHY' },
    governanceFiles: [
      { id: 'gf-1', fileType: 'SOUL' },
      { id: 'gf-2', fileType: 'ROLE' },
      { id: 'gf-3', fileType: 'HEARTBEAT' },
      { id: 'gf-4', fileType: 'MEMORY' },
      { id: 'gf-5', fileType: 'ETHICS' },
      { id: 'gf-6', fileType: 'COMPLIANCE' },
    ],
    subAgents: [
      { id: 'sa-1', name: 'Risk Analyzer', status: 'ACTIVE' },
      { id: 'sa-2', name: 'Policy Monitor', status: 'ACTIVE' },
      { id: 'sa-3', name: 'Audit Logger', status: 'ACTIVE' },
    ],
    backgroundJobs: [
      { id: 'bj-1', type: 'HEALTH_CHECK' },
      { id: 'bj-2', type: 'COMPLIANCE_SCAN' },
    ],
  },
  {
    id: 'demo-2',
    name: 'Marcus',
    title: 'Chief Technology Officer (CTO)',
    employeeId: 'CTO-002',
    status: 'ACTIVE',
    autonomyTier: 'SUPERVISED',
    riskLevel: 'MEDIUM',
    role: { name: 'Technology' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-2', status: 'RUNNING', healthStatus: 'HEALTHY' },
    governanceFiles: [
      { id: 'gf-7', fileType: 'SOUL' },
      { id: 'gf-8', fileType: 'ROLE' },
      { id: 'gf-9', fileType: 'HEARTBEAT' },
    ],
    subAgents: [
      { id: 'sa-4', name: 'Code Reviewer', status: 'ACTIVE' },
      { id: 'sa-5', name: 'Security Scanner', status: 'BUSY' },
    ],
    backgroundJobs: [
      { id: 'bj-3', type: 'HEALTH_CHECK' },
    ],
  },
  {
    id: 'demo-3',
    name: 'Aria',
    title: 'Chief Product Officer (CPO)',
    employeeId: 'CPO-003',
    status: 'ACTIVE',
    autonomyTier: 'SUPERVISED',
    riskLevel: 'LOW',
    role: { name: 'Product' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-3', status: 'RUNNING', healthStatus: 'HEALTHY' },
    governanceFiles: [
      { id: 'gf-10', fileType: 'SOUL' },
      { id: 'gf-11', fileType: 'ROLE' },
    ],
    subAgents: [
      { id: 'sa-6', name: 'User Research Agent', status: 'ACTIVE' },
      { id: 'sa-7', name: 'Feature Prioritizer', status: 'ACTIVE' },
    ],
    backgroundJobs: [],
  },
  {
    id: 'demo-4',
    name: 'Victor',
    title: 'Chief Financial Officer (CFO)',
    employeeId: 'CFO-004',
    status: 'PAUSED',
    autonomyTier: 'HUMAN_IN_LOOP',
    riskLevel: 'HIGH',
    role: { name: 'Finance' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-4', status: 'STOPPED', healthStatus: 'UNKNOWN' },
    governanceFiles: [
      { id: 'gf-12', fileType: 'SOUL' },
      { id: 'gf-13', fileType: 'ROLE' },
      { id: 'gf-14', fileType: 'COMPLIANCE' },
    ],
    subAgents: [
      { id: 'sa-8', name: 'Budget Analyzer', status: 'IDLE' },
    ],
    backgroundJobs: [],
  },
  {
    id: 'demo-5',
    name: 'Elena',
    title: 'Chief Legal Officer (CLO)',
    employeeId: 'CLO-005',
    status: 'ACTIVE',
    autonomyTier: 'HUMAN_IN_LOOP',
    riskLevel: 'HIGH',
    role: { name: 'Legal & Compliance' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-5', status: 'RUNNING', healthStatus: 'DEGRADED' },
    governanceFiles: [
      { id: 'gf-15', fileType: 'SOUL' },
      { id: 'gf-16', fileType: 'ROLE' },
      { id: 'gf-17', fileType: 'ETHICS' },
      { id: 'gf-18', fileType: 'COMPLIANCE' },
    ],
    subAgents: [
      { id: 'sa-9', name: 'Contract Reviewer', status: 'ACTIVE' },
      { id: 'sa-10', name: 'Regulatory Monitor', status: 'ACTIVE' },
    ],
    backgroundJobs: [
      { id: 'bj-4', type: 'COMPLIANCE_SCAN' },
    ],
  },
  {
    id: 'demo-6',
    name: 'Kai',
    title: 'Chief Information Security Officer (CISO)',
    employeeId: 'CISO-006',
    status: 'ACTIVE',
    autonomyTier: 'RESTRICTED',
    riskLevel: 'CRITICAL',
    role: { name: 'Security' },
    company: { name: 'CAO Platform' },
    governanceEngine: { id: 'ge-6', status: 'RUNNING', healthStatus: 'HEALTHY' },
    governanceFiles: [
      { id: 'gf-19', fileType: 'SOUL' },
      { id: 'gf-20', fileType: 'ROLE' },
      { id: 'gf-21', fileType: 'ETHICS' },
      { id: 'gf-22', fileType: 'COMPLIANCE' },
      { id: 'gf-23', fileType: 'HEARTBEAT' },
    ],
    subAgents: [
      { id: 'sa-11', name: 'Threat Detector', status: 'ACTIVE' },
      { id: 'sa-12', name: 'Access Auditor', status: 'ACTIVE' },
      { id: 'sa-13', name: 'Incident Responder', status: 'ACTIVE' },
    ],
    backgroundJobs: [
      { id: 'bj-5', type: 'HEALTH_CHECK' },
      { id: 'bj-6', type: 'COMPLIANCE_SCAN' },
      { id: 'bj-7', type: 'MEMORY_CLEANUP' },
    ],
  },
];

export default function EmployeesPage() {
  const [employees] = useState(DEMO_EMPLOYEES);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleDemoAction = () => {
    setShowAuthModal(true);
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employeeId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
        ${config.color === 'green' ? 'bg-green-900/50 text-green-400' :
          config.color === 'blue' ? 'bg-blue-900/50 text-blue-400' :
            config.color === 'yellow' ? 'bg-yellow-900/50 text-yellow-400' :
              config.color === 'red' ? 'bg-red-900/50 text-red-400' :
                config.color === 'purple' ? 'bg-purple-900/50 text-purple-400' :
                  'bg-gray-700 text-gray-400'}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getHealthBadge = (health) => {
    const config = HEALTH_CONFIG[health] || HEALTH_CONFIG.UNKNOWN;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs
        ${config.color === 'green' ? 'bg-green-900/30 text-green-400' :
          config.color === 'yellow' ? 'bg-yellow-900/30 text-yellow-400' :
            config.color === 'red' ? 'bg-red-900/30 text-red-400' :
              'bg-gray-800 text-gray-500'}`}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen pt-20 pb-12 relative">
      <AnimatedBackground />

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Full Access Required</h3>
              <p className="text-gray-400 text-sm">
                You're viewing a demo preview of the Digital Employees dashboard.
                Sign up or log in to manage your own AI workforce.
              </p>
            </div>
            <div className="space-y-3">
              <Link
                to="/signup"
                className="block w-full py-3 rounded-xl font-semibold text-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
              >
                Get Started Free
              </Link>
              <Link
                to="/demo"
                className="block w-full py-3 rounded-xl font-semibold text-center border border-slate-600 text-slate-300 hover:bg-slate-800 transition-all"
              >
                Try Interactive Chat Demo
              </Link>
              <button
                onClick={() => setShowAuthModal(false)}
                className="block w-full py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors text-center"
              >
                Continue Browsing Demo
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Demo Banner */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">
                Demo Preview
              </p>
              <p className="text-xs text-slate-400">
                You're viewing sample digital employees. Sign up to create and manage your own AI workforce.
              </p>
            </div>
          </div>
          <Link
            to="/signup"
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
          >
            Get Full Access
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Users className="w-8 h-8 text-cao-blue" />
              Digital Employees
            </h1>
            <p className="text-gray-400 mt-1">
              AI Executive Council - Governance-first autonomous workforce
            </p>
          </div>

          <button
            onClick={handleDemoAction}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5" />
            New Employee
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search employees..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-cao-blue"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="PROVISIONING">Provisioning</option>
              <option value="READY">Ready</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="text-2xl font-bold text-white">{employees.length}</div>
            <div className="text-xs text-gray-500">Total Employees</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="text-2xl font-bold text-green-400">
              {employees.filter(e => e.status === 'ACTIVE').length}
            </div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="text-2xl font-bold text-blue-400">
              {employees.reduce((sum, e) => sum + (e.subAgents?.length || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Sub-Agents</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="text-2xl font-bold text-purple-400">
              {employees.reduce((sum, e) => sum + (e.governanceFiles?.length || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Governance Files</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Employee List */}
          <div className="lg:col-span-2 space-y-4">
            {filteredEmployees.length === 0 ? (
              <GlowingCard className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Matching Employees</h3>
                <p className="text-gray-400 mb-4">
                  No employees match your current filters.
                </p>
              </GlowingCard>
            ) : (
              filteredEmployees.map((employee) => (
                <GlowingCard
                  key={employee.id}
                  className={`p-4 cursor-pointer transition-all ${selectedEmployee?.id === employee.id
                      ? 'ring-2 ring-cao-blue'
                      : 'hover:border-gray-600'
                    }`}
                  onClick={() => handleSelectEmployee(employee)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cao-blue to-cao-purple flex items-center justify-center text-white font-bold">
                        {employee.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{employee.name}</h3>
                        <p className="text-sm text-gray-400">{employee.title}</p>
                        <p className="text-xs text-gray-500 font-mono mt-1">{employee.employeeId}</p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(employee.status)}
                      {employee.governanceEngine && (
                        getHealthBadge(employee.governanceEngine.healthStatus)
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Bot className="w-4 h-4" />
                        {employee.role?.name || 'No Role'}
                      </span>
                      {employee.company && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-4 h-4" />
                          {employee.company.name}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {employee.status === 'ACTIVE' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDemoAction();
                          }}
                          className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-900/30 rounded transition-colors"
                          title="Pause (requires login)"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                      {employee.status === 'PAUSED' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDemoAction();
                          }}
                          className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-900/30 rounded transition-colors"
                          title="Resume (requires login)"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDemoAction();
                        }}
                        className="p-1.5 text-gray-400 hover:text-cao-blue hover:bg-cao-blue/10 rounded transition-colors"
                        title="Health Check (requires login)"
                      >
                        <Activity className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                </GlowingCard>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedEmployee ? (
              <GlowingCard className="p-6 sticky top-24">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedEmployee.name}</h2>
                      <p className="text-gray-400">{selectedEmployee.title}</p>
                    </div>
                    <button
                      onClick={() => setSelectedEmployee(null)}
                      className="p-1 text-gray-500 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedEmployee.status)}
                    {selectedEmployee.governanceEngine && (
                      getHealthBadge(selectedEmployee.governanceEngine.healthStatus)
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-800/50 rounded-lg">
                      <div className="text-2xl font-bold text-white">
                        {selectedEmployee.governanceFiles?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500">Governance Files</div>
                    </div>
                    <div className="p-3 bg-gray-800/50 rounded-lg">
                      <div className="text-2xl font-bold text-white">
                        {selectedEmployee.subAgents?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500">Sub-Agents</div>
                    </div>
                    <div className="p-3 bg-gray-800/50 rounded-lg">
                      <div className="text-2xl font-bold text-white">
                        {selectedEmployee.backgroundJobs?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500">Background Jobs</div>
                    </div>
                    <div className="p-3 bg-gray-800/50 rounded-lg">
                      <div className="text-sm font-medium text-cao-blue">
                        {selectedEmployee.autonomyTier?.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-gray-500">Autonomy</div>
                    </div>
                  </div>

                  {/* Governance Engine */}
                  {selectedEmployee.governanceEngine && (
                    <div className="p-4 bg-gray-800/30 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-300">Governance Engine</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${selectedEmployee.governanceEngine.status === 'RUNNING'
                            ? 'bg-green-900/50 text-green-400'
                            : selectedEmployee.governanceEngine.status === 'STOPPED'
                              ? 'bg-gray-700 text-gray-400'
                              : 'bg-yellow-900/50 text-yellow-400'
                          }`}>
                          {selectedEmployee.governanceEngine.status}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        {selectedEmployee.governanceEngine.status === 'RUNNING' ? (
                          <button
                            onClick={handleDemoAction}
                            className="flex-1 py-2 bg-red-900/30 text-red-400 rounded-lg text-sm hover:bg-red-900/50 transition-colors flex items-center justify-center gap-1"
                          >
                            <Pause className="w-4 h-4" />
                            Stop
                          </button>
                        ) : (
                          <button
                            onClick={handleDemoAction}
                            className="flex-1 py-2 bg-green-900/30 text-green-400 rounded-lg text-sm hover:bg-green-900/50 transition-colors flex items-center justify-center gap-1"
                          >
                            <Play className="w-4 h-4" />
                            Start
                          </button>
                        )}
                        <button
                          onClick={handleDemoAction}
                          className="flex-1 py-2 bg-cao-blue/20 text-cao-blue rounded-lg text-sm hover:bg-cao-blue/30 transition-colors flex items-center justify-center gap-1"
                        >
                          <Activity className="w-4 h-4" />
                          Health Check
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Sub-Agents */}
                  {selectedEmployee.subAgents?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-300 mb-2">Sub-Agents</h3>
                      <div className="space-y-2">
                        {selectedEmployee.subAgents.slice(0, 4).map((agent) => (
                          <div
                            key={agent.id}
                            className="flex items-center justify-between p-2 bg-gray-800/30 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <Bot className="w-4 h-4 text-cao-purple" />
                              <span className="text-sm text-white">{agent.name}</span>
                            </div>
                            <span className={`text-xs ${agent.status === 'ACTIVE' ? 'text-green-400' :
                                agent.status === 'BUSY' ? 'text-yellow-400' :
                                  'text-gray-500'
                              }`}>
                              {agent.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Governance Files */}
                  {selectedEmployee.governanceFiles?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-300 mb-2">Governance Files</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedEmployee.governanceFiles.map((file) => (
                          <span
                            key={file.id}
                            className="px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            {file.fileType}.md
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t border-gray-700 space-y-2">
                    <button
                      onClick={handleDemoAction}
                      className="w-full py-2 bg-cao-blue text-white rounded-lg text-sm hover:bg-cao-blue/80 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Full Details
                    </button>
                    <button
                      onClick={handleDemoAction}
                      className="w-full py-2 border border-gray-600 text-gray-300 rounded-lg text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Configure
                    </button>
                  </div>

                  {/* Demo Notice */}
                  <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-center">
                    <p className="text-xs text-blue-400/80">
                      This is demo data. Sign up to manage real digital employees.
                    </p>
                  </div>
                </div>
              </GlowingCard>
            ) : (
              <GlowingCard className="p-6 text-center">
                <Eye className="w-10 h-10 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Select an Employee</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Click on an employee to view details and explore the governance system.
                </p>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                  <p className="text-xs text-slate-500">
                    This demo showcases the CAO Platform's executive AI council with governance-first architecture.
                  </p>
                </div>
              </GlowingCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
